import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus, Car, Home, Smartphone, Package, Search,
    Eye, Edit2, Trash2, Loader2,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import { assetService } from '@services/assetService';
import CustomSelect from '../shared/CustomSelect';

const AssetsContent = () => {
    const navigate = useNavigate();
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [typeFilter, setTypeFilter] = useState('ALL');
    const [statistics, setStatistics] = useState(null);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [paginating, setPaginating] = useState(false);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(0); // Reset to first page on search
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchAssets();
    }, [currentPage, pageSize, debouncedSearchTerm, statusFilter, typeFilter]);

    useEffect(() => {
        fetchStatistics();
    }, []);

    const fetchAssets = useCallback(async () => {
        try {
            setPaginating(true);
            let response;

            // Handle search
            if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
                response = await assetService.clientAssets.search(debouncedSearchTerm);

                if (response.success && response.data) {
                    let filtered = response.data;

                    // Apply filters to search results
                    if (statusFilter !== 'ALL') {
                        filtered = filtered.filter(asset => asset.status === statusFilter);
                    }
                    if (typeFilter !== 'ALL') {
                        filtered = filtered.filter(asset => asset.assetType === typeFilter);
                    }

                    // Apply client-side pagination
                    const start = currentPage * pageSize;
                    const paginatedAssets = filtered.slice(start, start + pageSize);

                    setAssets(paginatedAssets);
                    setTotalElements(filtered.length);
                    setTotalPages(Math.ceil(filtered.length / pageSize));
                }
            } else {
                // No search - use filtered endpoint
                if (statusFilter !== 'ALL') {
                    // Get by status with pagination
                    response = await assetService.clientAssets.getByStatusPaginated(statusFilter, currentPage, pageSize);
                } else {
                    // Get all with pagination - use search with empty string
                    response = await assetService.clientAssets.searchPaginated('', currentPage, pageSize);
                }

                if (response.success && response.data) {
                    const pageData = response.data;

                    if (pageData.content) {
                        // Spring Boot Page format
                        let filtered = pageData.content;

                        // Apply type filter
                        if (typeFilter !== 'ALL') {
                            filtered = filtered.filter(asset => asset.assetType === typeFilter);
                        }

                        setAssets(filtered);
                        setTotalElements(pageData.totalElements);
                        setTotalPages(pageData.totalPages);
                    } else {
                        // Fallback for array response
                        let filtered = Array.isArray(pageData) ? pageData : [];

                        if (typeFilter !== 'ALL') {
                            filtered = filtered.filter(asset => asset.assetType === typeFilter);
                        }

                        setAssets(filtered);
                        setTotalElements(filtered.length);
                        setTotalPages(Math.ceil(filtered.length / pageSize));
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching assets:', error);
            setAssets([]);
            setTotalElements(0);
            setTotalPages(0);
        } finally {
            setPaginating(false);
            setLoading(false);
        }
    }, [currentPage, pageSize, debouncedSearchTerm, statusFilter, typeFilter]);

    const fetchStatistics = async () => {
        try {
            const response = await assetService.clientAssets.getStatistics();
            if (response.success && response.data) {
                setStatistics(response.data);
            }
        } catch (error) {
            console.error('Error fetching statistics:', error);
        }
    };

    const getAssetIcon = (type) => {
        switch (type) {
            case 'VEHICLE': return Car;
            case 'LAND':
            case 'PROPERTY': return Home;
            case 'PHONE': return Smartphone;
            default: return Package;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'VERIFIED': return 'bg-green-100 text-green-800 border-green-200';
            case 'PENDING': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'REJECTED': return 'bg-red-100 text-red-800 border-red-200';
            case 'UNDER_REVIEW': return 'bg-blue-100 text-blue-800 border-blue-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const handleDeleteAsset = async (id) => {
        if (window.confirm('Are you sure you want to delete this asset?')) {
            try {
                await assetService.clientAssets.delete(id);
                fetchAssets();
                fetchStatistics();
            } catch (error) {
                console.error('Error deleting asset:', error);
            }
        }
    };

    // Pagination Component
    const Pagination = () => {
        const startIndex = Math.min(currentPage * pageSize + 1, totalElements);
        const endIndex = Math.min((currentPage + 1) * pageSize, totalElements);

        return (
            <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex.toLocaleString()}</span> to{' '}
                        <span className="font-medium">{endIndex.toLocaleString()}</span> of{' '}
                        <span className="font-medium">{totalElements.toLocaleString()}</span> results
                    </span>

                    <CustomSelect
                        value={pageSize}
                        onChange={(val) => {
                            setPageSize(Number(val));
                            setCurrentPage(0);
                        }}
                        options={[
                            { value: 10, label: '10 per page' },
                            { value: 20, label: '20 per page' },
                            { value: 50, label: '50 per page' },
                            { value: 100, label: '100 per page' }
                        ]}
                        dropUp
                    />
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setCurrentPage(0)}
                        disabled={currentPage === 0 || paginating}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="First page"
                    >
                        <ChevronsLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 0 || paginating}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Previous page"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>

                    <span className="px-4 py-2 text-sm text-gray-700">
                        Page {(currentPage + 1).toLocaleString()} of {totalPages.toLocaleString() || 1}
                    </span>

                    <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage >= totalPages - 1 || paginating}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Next page"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setCurrentPage(totalPages - 1)}
                        disabled={currentPage >= totalPages - 1 || paginating}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Last page"
                    >
                        <ChevronsRight className="w-5 h-5" />
                    </button>

                    {paginating && (
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600 ml-2" />
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Asset Management</h2>
                    <p className="text-gray-600 mt-1">
                        {totalElements.toLocaleString()} total assets
                    </p>
                </div>
                <button
                    onClick={() => navigate('/compliance/assets/new')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                >
                    <Plus className="w-5 h-5" />
                    Add Asset
                </button>
            </div>

            {/* Statistics Cards */}
            {statistics && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Total Assets</p>
                        <p className="text-2xl font-bold text-gray-900">{statistics.totalAssets}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Pending</p>
                        <p className="text-2xl font-bold text-yellow-600">{statistics.pendingAssets}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Verified</p>
                        <p className="text-2xl font-bold text-green-600">{statistics.verifiedAssets}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Vehicles</p>
                        <p className="text-2xl font-bold text-gray-900">{statistics.vehicleAssets}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Properties</p>
                        <p className="text-2xl font-bold text-gray-900">
                            {statistics.landAssets + statistics.propertyAssets}
                        </p>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Search Assets
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by name, code, or client..."
                                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status
                        </label>
                        <CustomSelect
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={[
                                { value: 'ALL', label: 'All Statuses' },
                                { value: 'PENDING', label: 'Pending' },
                                { value: 'VERIFIED', label: 'Verified' },
                                { value: 'REJECTED', label: 'Rejected' },
                                { value: 'UNDER_REVIEW', label: 'Under Review' }
                            ]}
                            placeholder="All Statuses"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Asset Type
                        </label>
                        <CustomSelect
                            value={typeFilter}
                            onChange={setTypeFilter}
                            options={[
                                { value: 'ALL', label: 'All Types' },
                                { value: 'VEHICLE', label: 'Vehicles' },
                                { value: 'LAND', label: 'Land' },
                                { value: 'PROPERTY', label: 'Property' },
                                { value: 'PHONE', label: 'Phones' },
                                { value: 'ELECTRONICS', label: 'Electronics' },
                                { value: 'MACHINERY', label: 'Machinery' },
                                { value: 'OTHER', label: 'Other' }
                            ]}
                            placeholder="All Types"
                        />
                    </div>
                </div>
            </div>

            {/* Assets List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
                {paginating && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                )}

                {loading && assets.length === 0 ? (
                    <div className="flex items-center justify-center h-64">
                        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                    </div>
                ) : assets.length === 0 ? (
                    <div className="text-center py-12">
                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No assets found</p>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Asset
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Client
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Documents
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                        {assets.map((asset) => {
                            const Icon = getAssetIcon(asset.assetType);
                            return (
                                <tr key={asset.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                                <Icon className="w-5 h-5 text-gray-600" />
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {asset.requirementName}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {asset.assetCode}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{asset.clientName}</div>
                                        <div className="text-sm text-gray-500">{asset.clientCode}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                            {asset.assetType}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(asset.status)}`}>
                                            {asset.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <button
                                            onClick={() => navigate(`/compliance/assets/${asset.id}`)}
                                            className="text-blue-600 hover:text-blue-700 font-medium"
                                        >
                                            {asset.documents?.length || 0}
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => navigate(`/compliance/assets/${asset.id}`)}
                                                className="text-gray-600 hover:text-blue-600 transition"
                                                title="View Details"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => navigate(`/compliance/assets/${asset.id}/edit`)}
                                                className="text-gray-600 hover:text-indigo-600 transition"
                                                title="Edit Asset"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteAsset(asset.id)}
                                                className="text-gray-600 hover:text-red-600 transition"
                                                title="Delete Asset"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                )}

                {/* Pagination Controls */}
                {(totalPages > 0 || totalElements > 0) && <Pagination />}
            </div>
        </div>
    );
};

export default AssetsContent;
